import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sortBy } from 'lodash';

import Theme from 'LegacyWeployDesignLanguage/Theme';
import { LinkButton } from 'LegacyWeployDesignLanguage/buttons';

import DateRange from '../../common/DateRange';

const StyledDateRange = styled(DateRange)`
  margin-bottom: 0;
`;

const StyledLinkButton = styled(LinkButton)`
  color: ${props => props.theme.colors.text.primary};
  margin-left: ${props => props.theme.space.single};
  margin-top: ${props => props.theme.space.half};
  margin-bottom: ${props => props.theme.space.single};
`;

const Title = styled.div`
  color: ${props => props.theme.colors.text.primary};
  font-weight: 700;
`;

const BULLET_MARGIN = Theme.space.half;
const BULLET_DIAMETER = '18px';
const WORK_EXPERIENCES_ITEM_MARGIN = Theme.space.single;

const WorkExperiencesSummaryItem = styled.div`
  position: relative;
  margin-top: ${WORK_EXPERIENCES_ITEM_MARGIN};
  margin-bottom: ${WORK_EXPERIENCES_ITEM_MARGIN};
  margin-left: calc(${BULLET_DIAMETER} + (${BULLET_MARGIN}*2));

  &:before {
    display: block;
    content: '';
    border: 1px solid ${props => props.theme.colors.darkerGrey};
    width: ${BULLET_DIAMETER};
    height: ${BULLET_DIAMETER};
    border-radius: 50%;
    position: absolute;
    left: calc(-1*(${BULLET_DIAMETER} + ${BULLET_MARGIN}));
    top: 50%;
    transform: translateY(-50%);
    background-color: ${props => props.theme.colors.white};
    z-index: 2;
  }

  &:after {
    display: block;
    content: '';
    position: absolute;
    width: 1px;
    height: calc(100% + ${WORK_EXPERIENCES_ITEM_MARGIN});
    left: calc(-1*(${BULLET_DIAMETER} - (${BULLET_DIAMETER}/2) + ${BULLET_MARGIN}));
    top: 50%;
    background-color: ${props => props.theme.colors.darkerGrey};
    z-index: 1;
  }

  &:last-child {
    &:after {
      display: ${props => (props.truncated ? 'none' : 'block')};
      height: calc(25% + (${WORK_EXPERIENCES_ITEM_MARGIN}*2));
    }
  }
`;

const END_DATE_KEY = 'endDate';

const WorkExperiencesSummary = ({
  workExperiences,
  maxVisible,
  onLinkClicked,
  disabled,
}) => {
  const sortedWorkExperiences = sortBy(workExperiences, END_DATE_KEY).reverse();
  const truncatedWorkExperiences = sortedWorkExperiences.slice(0, maxVisible);
  const numWorkExperiences = workExperiences.length;

  return (
    <Fragment>
      <div>
        { truncatedWorkExperiences.map(({
          id,
          title,
          startDate,
          endDate,
          tasks,
        }) => (
          <WorkExperiencesSummaryItem
            truncated={numWorkExperiences <= maxVisible}
            key={id}
          >
            <Title>{title}</Title>
            <StyledDateRange
              startDate={startDate}
              endDate={endDate}
            />
            { !disabled &&
              <LinkButton onClick={onLinkClicked}>{`View ${tasks.length} tasks`}</LinkButton>
            }
          </WorkExperiencesSummaryItem>
        ))}
      </div>
      { numWorkExperiences > maxVisible &&
        <StyledLinkButton onClick={onLinkClicked}>{`View ${numWorkExperiences - maxVisible} more`}</StyledLinkButton>
      }
    </Fragment>
  );
};

WorkExperiencesSummary.propTypes = {
  workExperiences: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxVisible: PropTypes.number,
  onLinkClicked: PropTypes.func,
  disabled: PropTypes.bool,
};

WorkExperiencesSummary.defaultProps = {
  maxVisible: 2,
  onLinkClicked: () => {},
  disabled: false,
};

export default WorkExperiencesSummary;
